import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureBorrowerBureauConsent, captureBorrowerBureauConsentBulk } from '../../../../../store/company/action';
import { fetchLatestConsents } from '../../../../../store/dashboard/creditBureau/actions';
import { toast } from 'react-toastify';

export interface ConsentInfo {
    consenterName: string;
    consentedAt: string;
}

interface UseGiveConsentLogicParams {
    assesseeOrgId?: string;
}

export interface GiveConsentInput {
    pan?: string;
    mobileNumber?: string;
}

export type GiveConsentFn = (directorId: string, input?: GiveConsentInput, onSuccess?: () => void) => void;

interface UseGiveConsentLogicReturn {
    giveConsent: GiveConsentFn;
    latestConsentsByDirectorId: Record<string, ConsentInfo>;
    isLoading: boolean;
    receivedDirectorIds: Set<string>;
    // RECUR-27297 — per-PNO "consent mandatory" config, carried from the same
    // getConsentDetails response as latestConsentsByDirectorId.
    consentRequired: boolean;
    isBulkLoading: boolean;
    giveConsentForAll: (directorIds: string[]) => void;
}

function useGiveConsentLogic({ assesseeOrgId }: UseGiveConsentLogicParams): UseGiveConsentLogicReturn {
    const dispatch = useDispatch();
    const [receivedDirectorIds, setReceivedDirectorIds] = useState<Set<string>>(new Set());
    const [inFlightDirectorIds, setInFlightDirectorIds] = useState<Set<string>>(new Set());
    const [isBulkLoading, setIsBulkLoading] = useState(false);

    // Read latest consents from the canonical Redux slice — same store path used by the
    // PNO-side KeyOfficialsCreditSummary attribution badge, so both surfaces share one
    // source of truth and the fetchLatestConsents saga only runs once per orgId.
    const latestConsentsByDirectorId = useSelector((state: any) => state?.dashboard?.creditBureau?.latestConsents) as Record<
        string,
        ConsentInfo
    >;
    const consentRequired = useSelector((state: any) => !!state?.dashboard?.creditBureau?.consentRequired);

    useEffect(() => {
        if (assesseeOrgId) {
            dispatch(fetchLatestConsents({ orgId: assesseeOrgId }));
        }
    }, [assesseeOrgId, dispatch]);

    const giveConsent: GiveConsentFn = (directorId, input, onSuccess) => {
        if (!directorId || !assesseeOrgId) return;
        if (inFlightDirectorIds.has(directorId)) return;

        setInFlightDirectorIds((prev) => new Set([...prev, directorId]));

        dispatch(
            captureBorrowerBureauConsent({
                assesseeOrgId,
                directorId,
                pan: input?.pan,
                mobileNumber: input?.mobileNumber,
                onSuccess: () => {
                    setReceivedDirectorIds((prev) => new Set([...prev, directorId]));
                    setInFlightDirectorIds((prev) => {
                        const next = new Set(prev);
                        next.delete(directorId);
                        return next;
                    });
                    toast.success('Consent captured successfully');
                    // Re-fetch to surface "Last consent by X" line for everyone consuming the slice.
                    if (assesseeOrgId) {
                        dispatch(fetchLatestConsents({ orgId: assesseeOrgId }));
                    }
                    onSuccess?.();
                },
                onError: () => {
                    setInFlightDirectorIds((prev) => {
                        const next = new Set(prev);
                        next.delete(directorId);
                        return next;
                    });
                },
            }),
        );
    };

    // "Give Consent for All" — PAN/mobile are not a consent precondition (Fetch already
    // refuses to fire without them), so this call carries no identifiers or bureau
    // dimension, just the org and the rendered director ids (RECUR-27297).
    const giveConsentForAll = (directorIds: string[]) => {
        if (!assesseeOrgId || isBulkLoading || !directorIds?.length) return;

        setIsBulkLoading(true);

        dispatch(
            captureBorrowerBureauConsentBulk({
                assesseeOrgId,
                directorIds,
                // The endpoint returns the same generic success/error envelope as the
                // single-consent path — no per-director breakdown — so success means all
                // of them, and any failure comes back through onError with the BE's own
                // message ("Could not capture consent for N of M directors").
                onSuccess: () => {
                    setIsBulkLoading(false);
                    setReceivedDirectorIds((prev) => new Set([...prev, ...directorIds]));
                    toast.success('Consent captured for all directors');
                    if (assesseeOrgId) {
                        dispatch(fetchLatestConsents({ orgId: assesseeOrgId }));
                    }
                },
                onError: (message?: string) => {
                    setIsBulkLoading(false);
                    toast.error(message || 'Something went wrong');
                    // A partial failure still consented some directors, so refetch to
                    // show whatever actually landed rather than leaving stale rows.
                    if (assesseeOrgId) {
                        dispatch(fetchLatestConsents({ orgId: assesseeOrgId }));
                    }
                },
            }),
        );
    };

    return {
        giveConsent,
        latestConsentsByDirectorId: latestConsentsByDirectorId || {},
        isLoading: inFlightDirectorIds.size > 0,
        receivedDirectorIds,
        consentRequired,
        isBulkLoading,
        giveConsentForAll,
    };
}

export default useGiveConsentLogic;
